import {
	IconButton,
	Stack,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";
import { debounce } from "lodash-es";
import { Minus, Plus } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

const STEP = 1000;
const MAX = 9999999;

type Props = {
	defaultValue: number;
	setValue: (value: number) => void;
	disabled?: boolean;
};

export const IncrementalCounter = ({
	defaultValue,
	setValue,
	disabled,
}: Props) => {
	const theme = useTheme();
	const { t } = useResource();
	const [localValue, setLocalValue] = useState(defaultValue);
	const [editing, setEditing] = useState(false);

	const timerRef = useRef<NodeJS.Timeout | null>(null);
	const isChangingRef = useRef(false);
	const lastSetValueRef = useRef<number>(defaultValue);
	const lastTouchTimeRef = useRef<number>(0);

	const debouncedSetValue = useMemo(() => debounce(setValue, 200), [setValue]);

	useEffect(() => {
		debouncedSetValue(localValue);
		lastSetValueRef.current = localValue;
		return () => {
			debouncedSetValue.cancel();
		};
	}, [localValue, debouncedSetValue]);

	useEffect(() => {
		if (defaultValue !== lastSetValueRef.current) {
			setLocalValue(defaultValue);
			lastSetValueRef.current = defaultValue;
		}
	}, [defaultValue]);

	const nextLocalValue = useCallback((prev: number, delta: number) => {
		const next = Math.max(0, Math.min(MAX, prev + delta));
		return Math.floor(next / STEP) * STEP;
	}, []);

	const startChange = (delta: number) => {
		if (isChangingRef.current) return;
		isChangingRef.current = true;

		setLocalValue((prev) => nextLocalValue(prev, delta));

		let delay = 500;

		const run = () => {
			setLocalValue((prev) => nextLocalValue(prev, delta));

			delay = Math.max(50, delay - 50);
			timerRef.current = setTimeout(run, delay);
		};

		timerRef.current = setTimeout(run, delay);
	};

	const stopChange = () => {
		if (timerRef.current) clearTimeout(timerRef.current);
		isChangingRef.current = false;
	};

	const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const digits = e.target.value.replace(/\D/g, "");
		const num = digits ? Math.min(Number.parseInt(digits, 10), MAX) : 0;
		setLocalValue(num);
	};

	return (
		<Stack
			direction="row"
			alignItems="center"
			justifyContent="space-between"
			p={1.2}
			gap={0.4}
		>
			<IconButton
				onMouseDown={() => {
					if (Date.now() - lastTouchTimeRef.current < 500) return;
					startChange(-STEP);
				}}
				onTouchStart={(e) => {
					lastTouchTimeRef.current = Date.now();
					e.preventDefault();
					startChange(-STEP);
				}}
				onMouseUp={stopChange}
				onMouseLeave={stopChange}
				onTouchEnd={stopChange}
				disabled={localValue <= 0 || disabled}
				sx={{
					border: `2px solid ${
						disabled ? theme.palette.grey[500] : theme.palette.primary.main
					}`,
					p: 1.6,
				}}
			>
				<Minus
					size={16}
					color={
						disabled ? theme.palette.grey[500] : theme.palette.primary.main
					}
				/>
			</IconButton>

			{editing ? (
				<TextField
					disabled={disabled}
					autoFocus
					value={localValue === 0 ? "" : localValue.toLocaleString("cs-CZ")}
					onChange={handleFieldChange}
					onBlur={() => setEditing(false)}
					variant="standard"
					slotProps={{
						input: {
							disableUnderline: true,
							sx: { "& input": { p: 0, m: 0, height: "1.5em" } },
						},
					}}
					inputProps={{
						inputMode: "numeric",
						pattern: "[0-9]*",
						style: { fontSize: 40, fontWeight: 700, textAlign: "right" },
					}}
					sx={{ marginInline: 1 }}
				/>
			) : (
				<Stack
					direction="row"
					alignItems="flex-end"
					onClick={() => !disabled && setEditing(true)}
					sx={{ cursor: disabled ? "default" : "pointer" }}
				>
					<Typography
						fontSize={40}
						fontWeight={700}
						color={theme.palette.grey[900]}
					>
						{localValue.toLocaleString("cs-CZ")}
					</Typography>
					<Stack pb={1} pl={1}>
						<Typography
							fontSize={16}
							fontWeight={700}
							color={theme.palette.grey[900]}
						>
							{t(Resources.Common.CurrencySign)}
						</Typography>
					</Stack>
				</Stack>
			)}

			<IconButton
				onMouseDown={() => {
					if (Date.now() - lastTouchTimeRef.current < 500) return;
					startChange(STEP);
				}}
				onTouchStart={(e) => {
					lastTouchTimeRef.current = Date.now();
					e.preventDefault();
					startChange(STEP);
				}}
				onMouseUp={stopChange}
				onMouseLeave={stopChange}
				onTouchEnd={stopChange}
				disabled={localValue >= MAX || disabled}
				sx={{
					border: `2px solid ${
						disabled ? theme.palette.grey[500] : theme.palette.primary.main
					}`,
					p: 1.6,
				}}
			>
				<Plus
					size={16}
					color={
						disabled ? theme.palette.grey[500] : theme.palette.primary.main
					}
				/>
			</IconButton>
		</Stack>
	);
};
